export const GAME_CODES = ["lotto535", "mega645", "power655"] as const;

export type GameCode = (typeof GAME_CODES)[number];

export type GameDefinition = {
  code: GameCode;
  label: string;
  shortLabel: string;
  mainCount: number;
  mainMax: number;
  specialCount: number;
  specialMax: number | null;
  accent: string;
  scheduleLabel: string;
};

export const GAMES: Record<GameCode, GameDefinition> = {
  lotto535: {
    code: "lotto535",
    label: "Lotto 5/35",
    shortLabel: "5/35",
    mainCount: 5,
    mainMax: 35,
    specialCount: 1,
    specialMax: 12,
    accent: "#14b8a6",
    scheduleLabel: "13:00 và 21:00 hằng ngày"
  },
  mega645: {
    code: "mega645",
    label: "Mega 6/45",
    shortLabel: "6/45",
    mainCount: 6,
    mainMax: 45,
    specialCount: 0,
    specialMax: null,
    accent: "#f97316",
    scheduleLabel: "18:10 Thứ Tư, Thứ Sáu, Chủ nhật"
  },
  power655: {
    code: "power655",
    label: "Power 6/55",
    shortLabel: "6/55",
    mainCount: 6,
    mainMax: 55,
    specialCount: 1,
    specialMax: 55,
    accent: "#ef4444",
    scheduleLabel: "18:10 Thứ Ba, Thứ Năm, Thứ Bảy"
  }
};

export function getGame(code: GameCode): GameDefinition {
  return GAMES[code];
}

export function isGameCode(value: string): value is GameCode {
  return GAME_CODES.includes(value as GameCode);
}

export type NumberValidationResult =
  | { ok: true; numbers: number[] }
  | { ok: false; reason: string };

export function validateMainNumbers(
  code: GameCode,
  values: readonly number[]
): NumberValidationResult {
  const game = getGame(code);
  const numbers = [...values].sort((a, b) => a - b);

  if (numbers.length !== game.mainCount) {
    return {
      ok: false,
      reason: `${game.label} cần đúng ${game.mainCount} số chính.`
    };
  }

  if (new Set(numbers).size !== numbers.length) {
    return { ok: false, reason: "Các số chính không được trùng nhau." };
  }

  if (numbers.some((value) => !Number.isInteger(value) || value < 1 || value > game.mainMax)) {
    return {
      ok: false,
      reason: `Số chính phải nằm trong khoảng 01–${game.mainMax}.`
    };
  }

  return { ok: true, numbers };
}

export function validateSpecialNumber(
  code: GameCode,
  value: number | null | undefined
): NumberValidationResult {
  const game = getGame(code);
  if (game.specialCount === 0) {
    return value == null
      ? { ok: true, numbers: [] }
      : { ok: false, reason: `${game.label} không có số đặc biệt.` };
  }

  if (
    value == null ||
    !Number.isInteger(value) ||
    value < 1 ||
    game.specialMax == null ||
    value > game.specialMax
  ) {
    return {
      ok: false,
      reason: `Số đặc biệt phải nằm trong khoảng 01–${game.specialMax}.`
    };
  }

  return { ok: true, numbers: [value] };
}
